import { useMutation, useSuspenseQuery } from '@apollo/client';
import { produce } from 'immer';
import React, { useState } from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import { SettingsPageFrame } from '@eluve/blocks';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Input,
  useToast,
} from '@eluve/components';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useDialog } from '@eluve/utility-hooks';

import {
  createUserGroupMutation,
  getTenantUserGroupsQuery,
  tenantUserGroupsFragment,
} from './operations';

type Row = {
  id: string;
  name: string;
};

const cols = new ColDefBuilder<Row>()
  .detailsLink((r) => r.id)
  .defaultSortable('name')
  .build();

const CreateUserGroupDialog: React.FC = () => {
  const toast = useToast();
  const tenantId = useAssignedTenantIdFromParams();

  const { isDialogOpen, toggleDialog } = useDialog();
  const [newGroupName, setNewGroupName] = useState('');

  const [createUserGroup] = useMutation(createUserGroupMutation, {
    onError: () => toast.error('Failed to create user group'),
    optimisticResponse: () => ({
      insertUserGroupsOne: {
        __typename: 'UserGroups' as const,
        id: 'optimistic',
        name: newGroupName,
      },
    }),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: tenantUserGroupsFragment,
          key: {
            id: tenantId,
          },
        },
        (existing) => {
          const newGroup = data?.insertUserGroupsOne;
          if (!existing || !newGroup) {
            return existing;
          }

          return produce(existing, (draft) => {
            draft.user_groups.push(newGroup);
          });
        },
      );
    },
  });

  const handleCreateUserGroup = async () => {
    await createUserGroup({
      variables: {
        name: newGroupName,
      },
    });

    setNewGroupName('');
    toggleDialog();
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={toggleDialog} modal>
      <DialogTrigger asChild>
        <Button size="sm">
          <Icon name="Plus" size="auto" />
          Create User Group
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>New User Group</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Enter group name"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={toggleDialog}>
            Cancel
          </Button>
          <Button onClick={handleCreateUserGroup} disabled={!newGroupName}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserGroupsListContent: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();

  const { data } = useSuspenseQuery(getTenantUserGroupsQuery, {
    variables: {
      tenantId,
    },
  });

  return (
    <DataTable
      enableGlobalSearch
      columns={cols}
      data={data.tenantsByPk?.user_groups ?? []}
    />
  );
};

export const UserGroupsListPage: React.FC = () => (
  <SettingsPageFrame
    size="wide"
    title="User Groups"
    actions={<CreateUserGroupDialog />}
  >
    <UserGroupsListContent />
  </SettingsPageFrame>
);
